import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ORDER_STAGE_WORDS, emailsCustomer, nextStages } from '@sitzmark/core';
import type { OrderStage } from '@sitzmark/core';
import { useNavReset } from '../../components/nav-reset.js';
import { useNotice } from '../../components/notice.js';
import {
  Badge,
  Button,
  Callout,
  Card,
  Field,
  Loading,
  Modal,
  Mono,
  Screen,
  SectionNav,
} from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import type { ManagerOverride } from '../auth/manager-approval.js';
import { ManagerApproval, approvalRefusal, isOverridable } from '../auth/manager-approval.js';
import { ReceiptDialog } from '../sales/sales-screen.js';
import './online.css';

/**
 * Online orders (docs/STOREFRONT.md S5, decision 156).
 *
 * The web shop's paid orders, as a queue of work: oldest first, one card per
 * stage, and on each order only the next step core allows — so the screen
 * cannot offer a move the server would refuse. Handing an order over, or
 * shipping it, is where its sale completes; the receipt is a press away.
 */

type Order = Awaited<ReturnType<typeof api.onlineOrders.list.query>>[number];
type Move = 'picking' | 'ready' | 'shipped' | 'collected';

const OPEN_STAGES: OrderStage[] = ['new', 'picking', 'ready'];

/** What each stage card says when it is empty, and what it is for. */
const STAGE_HINTS: Partial<Record<OrderStage, string>> = {
  new: 'Paid, and nobody has started on them.',
  picking: 'Being gathered from the floor.',
  ready: 'Waiting for the customer to collect. Hand over with Collected.',
};

function placedWords(value: Date | string): string {
  const at = new Date(value);
  return at.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function OnlineOrdersScreen({
  registerId,
  onReturnStarted,
  onBackToSales,
}: {
  /** The till this is done at, recorded on a hand-over; null when none is chosen. */
  registerId: string | null;
  onReturnStarted: () => void;
  /** Online orders live under Sales (decision 159); this is the way back. */
  onBackToSales: () => void;
}) {
  const [view, setView] = useState<'open' | 'done'>('open');
  const cardMode = useQuery({
    queryKey: ['payments', 'registerMode'],
    queryFn: () => api.payments.registerMode.query(),
    staleTime: 30_000,
  });
  const [receiptFor, setReceiptFor] = useState<string | null>(null);
  useNavReset(() => {
    setView('open');
    setReceiptFor(null);
  });

  const orders = useQuery({
    queryKey: ['onlineOrders', 'list', view],
    queryFn: () => api.onlineOrders.list.query({ view }),
  });

  return (
    <Screen
      title="Online orders"
      subtitle="Paid on the web shop. Oldest first — the order the work should be done in."
      width="wide"
      secondaryActions={
        <Button variant="ghost" onClick={onBackToSales}>
          ← Sales
        </Button>
      }
    >
      {/* The brake (decision 232): said where the online work is done, not only in Settings. */}
      {cardMode.data?.onlinePaused === true ? (
        <Callout tone="danger" title="Online card payments are paused">
          {cardMode.data.onlinePausedReason} Shoppers are asked to call. An admin looks at the
          attempts and turns them back on in Settings → Card payments.
        </Callout>
      ) : null}
      <SectionNav
        label="Online orders"
        current={view}
        items={[
          { key: 'open', label: 'To do' },
          { key: 'done', label: 'Done' },
        ]}
        onChange={setView}
      />

      {orders.isPending ? (
        <Loading label="Loading orders" />
      ) : orders.isError ? (
        <Callout tone="danger">{errorMessage(orders.error)}</Callout>
      ) : view === 'open' ? (
        <div className="online__stages">
          {OPEN_STAGES.map((stage) => {
            const here = orders.data.filter((order) => order.stage === stage);
            return (
              <Card
                key={stage}
                title={
                  <>
                    {ORDER_STAGE_WORDS[stage].label} <Badge tone="neutral">{here.length}</Badge>
                  </>
                }
              >
                {here.length === 0 ? (
                  <p className="text-secondary">{STAGE_HINTS[stage]} None right now.</p>
                ) : (
                  <ul className="online__list">
                    {here.map((order) => (
                      <li key={order.id}>
                        <OrderCard order={order} registerId={registerId} />
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            );
          })}
        </div>
      ) : orders.data.length === 0 ? (
        <p className="text-secondary">Nothing has been collected, shipped or canceled yet.</p>
      ) : (
        <ul className="online__list">
          {orders.data.map((order) => (
            <li key={order.id}>
              <OrderCard
                order={order}
                registerId={registerId}
                onReceipt={order.saleId === null ? undefined : () => setReceiptFor(order.saleId)}
              />
            </li>
          ))}
        </ul>
      )}

      {receiptFor !== null ? (
        <ReceiptDialog
          saleId={receiptFor}
          onClose={() => setReceiptFor(null)}
          onReturnStarted={onReturnStarted}
        />
      ) : null}
    </Screen>
  );
}

function OrderCard({
  order,
  registerId,
  onReceipt,
}: {
  order: Order;
  registerId: string | null;
  onReceipt?: (() => void) | undefined;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [shipping, setShipping] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['onlineOrders'] });
    void queryClient.invalidateQueries({ queryKey: ['sales'] });
  };

  const move = useMutation({
    mutationFn: (input: { to: Move; trackingNumber?: string }) =>
      api.onlineOrders.move.mutate({
        orderId: order.id,
        to: input.to,
        registerId,
        ...(input.trackingNumber === undefined ? {} : { trackingNumber: input.trackingNumber }),
      }),
    onSuccess: (result, input) => {
      recordBreadcrumb('online', `${order.number} → ${input.to}`);
      notify({
        title:
          input.to === 'picking'
            ? `${order.number} — picking started`
            : `${order.number} marked ${ORDER_STAGE_WORDS[input.to].label.toLowerCase()}`,
        ...(emailsCustomer(input.to) ? { detail: `${order.contact.name} is emailed.` } : {}),
      });
      setShipping(false);
      refresh();
      void result;
    },
    // Somebody else got there first: the list is out of date, so bring it back.
    onError: refresh,
  });

  const forward = nextStages(order.stage, order.fulfillment).filter(
    (stage): stage is Move => stage !== 'canceled',
  );
  const cancellable = nextStages(order.stage, order.fulfillment).includes('canceled');

  return (
    <article className="online-order" aria-label={`Order ${order.number}`}>
      <header className="online-order__head">
        <Mono>{order.number}</Mono>
        <strong>{order.contact.name}</strong>
        <Badge tone={order.fulfillment === 'ship' ? 'warning' : 'neutral'}>
          {order.fulfillment === 'ship' ? 'Ship' : 'Pickup'}
        </Badge>
        {order.stage === 'canceled' ? (
          <Badge tone="danger">
            {order.refundPending ? 'Canceled — refund pending' : 'Canceled'}
          </Badge>
        ) : OPEN_STAGES.includes(order.stage) ? null : (
          <Badge tone="success">{ORDER_STAGE_WORDS[order.stage].label}</Badge>
        )}
        <span className="text-secondary online-order__when">
          Placed {placedWords(order.placedAt)}
        </span>
      </header>

      <div className="online-order__body">
        <ul className="online-order__lines">
          {order.lines.map((line, index) => (
            <li key={index}>
              <span className="online-order__qty">{line.quantity} ×</span> {line.description}
              {line.sku === null ? null : (
                <>
                  {' '}
                  <Mono>{line.sku}</Mono>
                </>
              )}
            </li>
          ))}
        </ul>
        <div className="online-order__who">
          <div>{order.contact.phone}</div>
          <div>{order.contact.email}</div>
          {order.address === null ? null : (
            <address>
              {order.address.line1}
              {order.address.line2 === '' ? null : <>, {order.address.line2}</>}
              <br />
              {order.address.city}, {order.address.state} {order.address.postalCode}
            </address>
          )}
          {order.trackingNumber === null ? null : (
            <div>
              Tracking <Mono>{order.trackingNumber}</Mono>
            </div>
          )}
          {order.cancelReason === null ? null : (
            <div className="text-secondary">Canceled: {order.cancelReason}</div>
          )}
        </div>
        <div className="online-order__paid">
          <div className="label-micro">Paid by card</div>
          <div className="money">{money(order.amountCents)}</div>
        </div>
      </div>

      {move.isError ? <Callout tone="danger">{errorMessage(move.error)}</Callout> : null}

      {forward.length > 0 || cancellable || onReceipt !== undefined ? (
        <footer className="online-order__actions">
          {cancellable ? (
            <Button variant="ghost" onClick={() => setCanceling(true)}>
              Cancel order
            </Button>
          ) : null}
          {onReceipt !== undefined && order.stage !== 'canceled' ? (
            <Button variant="secondary" onClick={onReceipt}>
              Receipt
            </Button>
          ) : null}
          {forward.map((stage) => (
            <Button
              key={stage}
              variant="primary"
              busy={move.isPending}
              onClick={() => (stage === 'shipped' ? setShipping(true) : move.mutate({ to: stage }))}
            >
              {ORDER_STAGE_WORDS[stage].action}
            </Button>
          ))}
        </footer>
      ) : null}

      {shipping ? (
        <ShipDialog
          number={order.number}
          busy={move.isPending}
          onCancel={() => setShipping(false)}
          onShip={(trackingNumber) =>
            move.mutate({
              to: 'shipped',
              ...(trackingNumber === '' ? {} : { trackingNumber }),
            })
          }
        />
      ) : null}

      {canceling ? (
        <CancelDialog
          order={order}
          registerId={registerId}
          onDone={() => {
            setCanceling(false);
            refresh();
          }}
          onClose={() => setCanceling(false)}
        />
      ) : null}
    </article>
  );
}

function ShipDialog({
  number,
  busy,
  onCancel,
  onShip,
}: {
  number: string;
  busy: boolean;
  onCancel: () => void;
  onShip: (trackingNumber: string) => void;
}) {
  const [tracking, setTracking] = useState('');
  return (
    <Modal
      open
      title={`Ship ${number}`}
      description="The customer is emailed that it is on its way. This completes the sale."
      onClose={onCancel}
      secondaryAction={
        <Button variant="ghost" onClick={onCancel}>
          Not yet
        </Button>
      }
      primaryAction={
        <Button variant="primary" busy={busy} onClick={() => onShip(tracking.trim())}>
          Mark shipped
        </Button>
      }
    >
      <Field label="Tracking number" hint="Optional. It goes in the customer's email.">
        {(props) => (
          <input
            {...props}
            className="input"
            value={tracking}
            maxLength={100}
            onChange={(event) => setTracking(event.target.value)}
          />
        )}
      </Field>
    </Modal>
  );
}

function CancelDialog({
  order,
  registerId,
  onDone,
  onClose,
}: {
  order: Order;
  registerId: string | null;
  onDone: () => void;
  onClose: () => void;
}) {
  const notify = useNotice();
  const [reason, setReason] = useState('');
  const [needsApproval, setNeedsApproval] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);

  const cancel = useMutation({
    mutationFn: (override?: ManagerOverride) =>
      api.onlineOrders.cancel.mutate({
        orderId: order.id,
        reason: reason.trim(),
        registerId,
        ...(override === undefined ? {} : { override }),
      }),
    onSuccess: (result) => {
      recordBreadcrumb('online', `${order.number} canceled`);
      setNeedsApproval(false);
      if (result.refunded) {
        notify({
          title: `${order.number} canceled and refunded`,
          detail: `${money(order.amountCents)} back to the card. Its stock is back on sale.`,
        });
        onDone();
        return;
      }
      // Canceled, and the card not refunded yet: say so, and keep trying.
      setOutcome(
        `${order.number} is canceled and its stock is back on sale, but the refund did not go through yet (${result.reason ?? 'no reason given'}). It is retried every minute, and the customer is emailed when it does.`,
      );
    },
    onError: (error) => {
      if (isOverridable(error)) setNeedsApproval(true);
    },
  });

  if (outcome !== null) {
    return (
      <Modal
        open
        title="Refund pending"
        onClose={onDone}
        primaryAction={
          <Button variant="primary" onClick={onDone}>
            Done
          </Button>
        }
      >
        <Callout tone="warning">{outcome}</Callout>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        open={!needsApproval}
        title={`Cancel ${order.number}?`}
        description={`${money(order.amountCents)} goes back to the customer's card, and the stock goes back on sale.`}
        tone="danger"
        onClose={onClose}
        secondaryAction={
          <Button variant="ghost" onClick={onClose}>
            Keep the order
          </Button>
        }
        primaryAction={
          <Button
            variant="danger"
            busy={cancel.isPending}
            {...(reason.trim() === '' ? { disabledReason: 'Say why, for the customer.' } : {})}
            onClick={() => cancel.mutate(undefined)}
          >
            Cancel and refund
          </Button>
        }
      >
        <Field label="Why" hint="The customer reads this in their email.">
          {(props) => (
            <textarea
              {...props}
              className="input"
              rows={3}
              maxLength={300}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </Field>
        {cancel.isError && !isOverridable(cancel.error) ? (
          <Callout tone="danger">{errorMessage(cancel.error)}</Callout>
        ) : null}
      </Modal>
      {needsApproval ? (
        <ManagerApproval
          title="Manager approval needed"
          because="Canceling an online order refunds the customer's card."
          busy={cancel.isPending}
          error={cancel.isError ? approvalRefusal(cancel.error) : null}
          onCancel={() => setNeedsApproval(false)}
          onApprove={(override) => cancel.mutate(override)}
        />
      ) : null}
    </>
  );
}
